import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Circle, Crosshair, Eye, Link2, Plus, Save, Sparkles, Trash2, Upload, X } from 'lucide-react'
import { ServerApiClient } from '../../../data/ServerApiClient'
import { parseCfoRouteState, CFO_LIGHTWEIGHT_PAGE_IDS, type CfoPageSlug } from '../../parity-dev/cfo/cfoRoutes'
import { validateTourDoc } from '../tourSchema'
import { buildRailAnchorCheck } from '../anchor'
import {
  DEFAULT_HIGHLIGHT,
  DEFAULT_POSITION,
  flattenTourSteps,
  type TourDoc,
  type TourStep,
  type TourWidgetKind,
} from '../tourTypes'
import { ElementPicker } from './ElementPicker'
import { FlowRecorder } from './FlowRecorder'
import { TourShareLinksPanel } from './TourShareLinksPanel'
import { jsxSnippetFor, type AnchorCandidate } from './pickAnchor'
import './TourAuthorRail.css'

/**
 * In-app tour authoring, opened with `?fg_tour_author=1`.
 *
 * The loop is: bind an anchor by clicking the real element, type the copy, pick
 * a widget, preview through the *real* engine, save a draft, publish. There is
 * deliberately no separate preview renderer — a preview that is not the runtime
 * is a preview that lies.
 *
 * Publishing is blocked until anchor verification has passed for the version,
 * which is enforced server-side too; this rail only explains why.
 */

export type TourAuthorRailProps = {
  doc: TourDoc
  onChange: (doc: TourDoc) => void
  onPreview: (stepId: string) => void
  onClose: () => void
}

const WIDGETS: Array<{ kind: TourWidgetKind; label: string; note: string }> = [
  { kind: 'tooltip', label: 'Tooltip', note: 'Anchored, with a spotlight' },
  { kind: 'hotspot', label: 'Hotspot', note: 'A pulse the visitor clicks' },
  { kind: 'text_modal', label: 'Text modal', note: 'Centered — open or close with this' },
  { kind: 'media_modal', label: 'Media modal', note: 'An image or video' },
  { kind: 'lead_form', label: 'Lead form', note: 'Captures contact details' },
  { kind: 'none', label: 'No guide', note: 'Advance on any click' },
]

const blankStep = (id: string, page: CfoPageSlug): TourStep => ({
  id,
  title: 'New step',
  route: { page },
  anchor: null,
  fallback: 'modal',
  advance: { kind: 'cta' },
  widget: { kind: 'text_modal', title: 'New step', body: '' },
})

export function TourAuthorRail({ doc, onChange, onPreview, onClose }: TourAuthorRailProps) {
  const api = useMemo(() => new ServerApiClient(), [])
  const steps = useMemo(() => flattenTourSteps(doc), [doc])
  const [selectedId, setSelectedId] = useState<string>(() => steps[0]?.step.id || '')
  const [picking, setPicking] = useState(false)
  const [candidate, setCandidate] = useState<AnchorCandidate | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [draftVersion, setDraftVersion] = useState<number | null>(null)
  const [showShareLinks, setShowShareLinks] = useState(false)

  const selected = steps.find((s) => s.step.id === selectedId) || steps[0] || null
  const validation = useMemo(() => validateTourDoc(doc), [doc])

  /** Replace one step in place, preserving chapter structure. */
  const updateStep = useCallback((stepId: string, patch: Partial<TourStep>) => {
    onChange({
      ...doc,
      chapters: doc.chapters.map((chapter) => ({
        ...chapter,
        steps: chapter.steps.map((step) => (step.id === stepId ? { ...step, ...patch } : step)),
      })),
    })
  }, [doc, onChange])

  const updateWidget = useCallback((stepId: string, patch: Record<string, unknown>) => {
    const step = steps.find((s) => s.step.id === stepId)?.step
    if (!step) return
    updateStep(stepId, { widget: { ...step.widget, ...patch } as TourStep['widget'] })
  }, [steps, updateStep])

  const changeWidgetKind = useCallback((stepId: string, kind: TourWidgetKind) => {
    const step = steps.find((s) => s.step.id === stepId)?.step
    if (!step) return
    // Carry shared fields across so switching kind is not a retype.
    const title = 'title' in step.widget ? step.widget.title || '' : step.title
    const body = 'body' in step.widget ? step.widget.body || '' : ''
    const base = { kind } as Record<string, unknown>
    if (kind === 'tooltip') Object.assign(base, { body, highlight: DEFAULT_HIGHLIGHT, position: DEFAULT_POSITION })
    else if (kind === 'hotspot') Object.assign(base, { body, highlight: DEFAULT_HIGHLIGHT, position: DEFAULT_POSITION })
    else if (kind === 'text_modal') Object.assign(base, { title: title || step.title, body })
    else if (kind === 'media_modal') Object.assign(base, { title, body, media: { kind: 'image', src: '', alt: '' } })
    else if (kind === 'lead_form') Object.assign(base, { title: title || 'Keep going', body })
    updateStep(stepId, { widget: base as TourStep['widget'] })
  }, [steps, updateStep])

  const [recording, setRecording] = useState(false)
  const recordStep = useCallback((step: TourStep) => {
    // Append to the last chapter, same as addStep — recording is just a
    // faster hand on the same lever.
    const chapters = doc.chapters.slice()
    const last = chapters[chapters.length - 1]
    chapters[chapters.length - 1] = { ...last, steps: [...last.steps, step] }
    onChange({ ...doc, chapters })
    setSelectedId(step.id)
  }, [doc, onChange])

  const addStep = useCallback(() => {
    const route = parseCfoRouteState(window.location)
    const page = (route?.page || 'ceo') as CfoPageSlug
    const id = `step-${Date.now().toString(36)}`
    const chapters = doc.chapters.slice()
    const last = chapters[chapters.length - 1]
    chapters[chapters.length - 1] = { ...last, steps: [...last.steps, blankStep(id, page)] }
    onChange({ ...doc, chapters })
    setSelectedId(id)
  }, [doc, onChange])

  const moveStep = useCallback((stepId: string, delta: -1 | 1) => {
    // Reorder within the chapter; the analytics key is the step id, so a move
    // never breaks history — only the walk order changes.
    onChange({
      ...doc,
      chapters: doc.chapters.map((chapter) => {
        const i = chapter.steps.findIndex((st) => st.id === stepId)
        if (i < 0) return chapter
        const j = i + delta
        if (j < 0 || j >= chapter.steps.length) return chapter
        const steps = chapter.steps.slice()
        ;[steps[i], steps[j]] = [steps[j], steps[i]]
        return { ...chapter, steps }
      }),
    })
  }, [doc, onChange])

  const deleteStep = useCallback((stepId: string) => {
    const chapters = doc.chapters.map((c) => ({ ...c, steps: c.steps.filter((s) => s.id !== stepId) }))
    // A chapter with no steps fails validation; drop it with its last step.
    onChange({ ...doc, chapters: chapters.filter((c) => c.steps.length > 0) })
  }, [doc, onChange])

  const applyAnchor = useCallback((picked: AnchorCandidate) => {
    setCandidate(picked)
    setPicking(false)
    if (!selected) return
    updateStep(selected.step.id, { anchor: picked.anchor })
  }, [selected, updateStep])

  const draftNarration = useCallback(async () => {
    setBusy(true)
    setStatus(null)
    try {
      const out = await api.draftTourNarration({ doc })
      let filled = 0
      const byId = new Map(out.drafts.map((d) => [d.id, d]))
      onChange({
        ...doc,
        chapters: doc.chapters.map((chapter) => ({
          ...chapter,
          steps: chapter.steps.map((step) => {
            const draft = byId.get(step.id)
            if (!draft) return step
            // Drafts fill EMPTY fields only — the author's words always win.
            const next = { ...step }
            if (!step.title || step.title === 'New step') { next.title = draft.title; filled++ }
            const body = 'body' in step.widget ? step.widget.body || '' : ''
            if ('body' in step.widget && !body && draft.body) {
              next.widget = { ...step.widget, body: draft.body } as TourStep['widget']
              filled++
            }
            if (!step.voice?.script && draft.voice_script) {
              next.voice = { script: draft.voice_script }
              filled++
            }
            return next
          }),
        })),
      })
      setStatus(`Drafted with ${out.provenance.model_id}: filled ${filled} empty field(s). Review before saving — drafts never overwrite your words.`)
    } catch (err: unknown) {
      setStatus(String((err as Error)?.message || 'Drafting failed.'))
    } finally {
      setBusy(false)
    }
  }, [api, doc, onChange])

  const save = useCallback(async () => {
    setBusy(true)
    setStatus(null)
    try {
      const out = await api.saveTourDraft({ slug: doc.slug, name: doc.name, context: doc.context, doc })
      setDraftVersion(out.version)
      setStatus(`Saved draft v${out.version}.`)
    } catch (err: unknown) {
      setStatus(String((err as Error)?.message || 'Save failed.'))
    } finally {
      setBusy(false)
    }
  }, [api, doc])

  const publish = useCallback(async () => {
    if (draftVersion == null) { setStatus('Save a draft first.'); return }
    setBusy(true)
    setStatus(null)
    try {
      // Attest to the anchors we can actually see from this browser so the
      // server gate clears without the CI verifier having uploaded first. Only
      // a genuine pass is attached — a fail is never sent, so it can't clobber a
      // stored full-tour check via the server's COALESCE, and the server still
      // gets to accept a prior verifier pass. On a rail fail we still attempt
      // publish (a stored pass may carry it) but lead with the specific reason.
      const check = buildRailAnchorCheck(doc, window.location.pathname)
      await api.publishTour({
        slug: doc.slug,
        version: draftVersion,
        anchor_check: check.status === 'pass' ? check : undefined,
      })
      const deferredNote = check.deferred.length
        ? ` ${check.verified.length} step(s) verified on this page; ${check.deferred.length} on other pages — run \`npm run tour:verify-anchors\` for the full check.`
        : ''
      setStatus(`Published v${draftVersion}.${deferredNote}`)
    } catch (err: unknown) {
      // The server refuses to publish without a passing anchor check. Prefer the
      // rail's specific reason (which anchor, which page) over the generic 400.
      const railReason = buildRailAnchorCheck(doc, window.location.pathname).reason
      const serverMsg = String((err as Error)?.message || 'Publish failed.')
      setStatus(railReason && /anchor/i.test(serverMsg) ? `${serverMsg} · ${railReason}` : serverMsg)
    } finally {
      setBusy(false)
    }
  }, [api, doc, draftVersion])

  useEffect(() => {
    if (!selected && steps.length) setSelectedId(steps[0].step.id)
  }, [selected, steps])

  const widget = selected?.step.widget
  const stepIssues = validation.errors.filter((e) => selected && e.path.includes(`steps[`) && e.message)

  return (
    <>
      {picking ? <ElementPicker onPick={applyAnchor} onCancel={() => setPicking(false)} /> : null}
      {recording ? (
        <FlowRecorder
          recordedCount={steps.filter((s) => s.step.id.startsWith('rec-')).length}
          onRecord={recordStep}
          onStop={() => setRecording(false)}
        />
      ) : null}
      <aside className="fg-author" aria-label="Tour authoring">
        <div className="fg-author__head">
          <h2 className="fg-author__title">{doc.name}</h2>
          <button className="fg-author__btn" onClick={onClose} aria-label="Close authoring"><X size={13} /></button>
        </div>

        <div className="fg-author__body">
          <div className="fg-author__field">
            <span className="fg-author__label">Steps</span>
            <div className="fg-author__steps">
              {steps.map(({ step, index }) => (
                <button
                  key={step.id}
                  className={`fg-author__step${step.id === selected?.step.id ? ' is-active' : ''}`}
                  onClick={() => setSelectedId(step.id)}
                >
                  <span style={{ opacity: 0.6 }}>{index + 1}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {step.title || step.id}
                  </span>
                  {step.anchor ? null : <span title="No anchor — renders centered">◇</span>}
                </button>
              ))}
            </div>
            <button className="fg-author__btn" onClick={addStep}><Plus size={13} /> Add step here</button>
            <button
              className={`fg-author__btn${recording ? ' is-active' : ''}`}
              onClick={() => setRecording((r) => !r)}
              title="Click through the flow in the app; each click becomes a draft step"
            >
              <Circle size={13} /> {recording ? 'Stop recording' : 'Record flow'}
            </button>
            <button className="fg-author__btn" onClick={() => void draftNarration()} disabled={busy}
              title="AI-draft title, body and voice script for steps with empty fields">
              <Sparkles size={13} /> Draft narration
            </button>
          </div>

          {selected ? (
            <>
              <div className="fg-author__field">
                <span className="fg-author__label">Title</span>
                <input
                  className="fg-author__input"
                  value={selected.step.title}
                  onChange={(e) => updateStep(selected.step.id, { title: e.target.value })}
                />
              </div>

              <div className="fg-author__field">
                <span className="fg-author__label">Widget</span>
                <select
                  className="fg-author__select"
                  value={widget?.kind}
                  onChange={(e) => changeWidgetKind(selected.step.id, e.target.value as TourWidgetKind)}
                >
                  {WIDGETS.map((w) => (
                    <option key={w.kind} value={w.kind}>{w.label} — {w.note}</option>
                  ))}
                </select>
              </div>

              {widget && 'body' in widget ? (
                <div className="fg-author__field">
                  <span className="fg-author__label">Body</span>
                  <textarea
                    className="fg-author__textarea"
                    value={widget.body || ''}
                    onChange={(e) => updateWidget(selected.step.id, { body: e.target.value })}
                    placeholder="What should the visitor understand here?"
                  />
                  <p className="fg-author__note">
                    Tokens: {'{{first_name}}'}, {'{{$MMMM d|+30d}}'}, {'{{fact:gross_margin_pct}}'}
                  </p>
                </div>
              ) : null}

              <div className="fg-author__field">
                <span className="fg-author__label">Page</span>
                <select
                  className="fg-author__select"
                  value={selected.step.route.page}
                  onChange={(e) => updateStep(selected.step.id, {
                    route: { ...selected.step.route, page: e.target.value as CfoPageSlug },
                  })}
                >
                  {CFO_LIGHTWEIGHT_PAGE_IDS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div className="fg-author__field">
                <span className="fg-author__label">Anchor</span>
                <button className="fg-author__btn" onClick={() => setPicking(true)}>
                  <Crosshair size={13} /> {selected.step.anchor ? 'Rebind anchor' : 'Bind anchor'}
                </button>
                {selected.step.anchor ? (
                  <p className="fg-author__note">{selected.step.anchor.label} · {selected.step.anchor.kind}</p>
                ) : (
                  <p className="fg-author__note">No anchor — this step renders as a centered modal.</p>
                )}
                {candidate ? (
                  <div className={`fg-author__grade fg-author__grade--${candidate.grade}`}>
                    {candidate.advice}
                    {candidate.matches > 1 ? ` Matches ${candidate.matches} elements — narrow it before publishing.` : ''}
                    {candidate.suggestedAttribute ? (
                      <>
                        <br />Ask an engineer to add this to the element:
                        <code className="fg-author__snippet">{jsxSnippetFor(candidate.suggestedAttribute)}</code>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="fg-author__field">
                <span className="fg-author__label">If the anchor is missing</span>
                <select
                  className="fg-author__select"
                  value={selected.step.fallback}
                  onChange={(e) => updateStep(selected.step.id, { fallback: e.target.value as TourStep['fallback'] })}
                >
                  <option value="modal">Show it centered (default)</option>
                  <option value="skip">Skip the step — for data that may not exist</option>
                  <option value="block">Show a "being updated" card</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                <button className="fg-author__btn" onClick={() => moveStep(selected.step.id, -1)} aria-label="Move step up">
                  <ArrowUp size={13} />
                </button>
                <button className="fg-author__btn" onClick={() => moveStep(selected.step.id, 1)} aria-label="Move step down">
                  <ArrowDown size={13} />
                </button>
                <button className="fg-author__btn" onClick={() => onPreview(selected.step.id)}>
                  <Eye size={13} /> Play from here
                </button>
              </div>
              <button className="fg-author__btn" onClick={() => deleteStep(selected.step.id)}>
                <Trash2 size={13} /> Delete step
              </button>
            </>
          ) : (
            <p className="fg-author__note">This tour has no steps yet.</p>
          )}

          {validation.errors.length ? (
            <ul className="fg-author__issues">
              {validation.errors.slice(0, 6).map((e, i) => <li key={i}>{e.message}</li>)}
            </ul>
          ) : null}
          {stepIssues.length === 0 && validation.warnings.length ? (
            <p className="fg-author__note">{validation.warnings[0].message}</p>
          ) : null}
        </div>

        <div className="fg-author__field" style={{ padding: '0 14px 10px' }}>
          <button className="fg-author__btn" onClick={() => setShowShareLinks((v) => !v)}>
            <Link2 size={13} /> {showShareLinks ? 'Hide share links' : 'Share links'}
          </button>
          {showShareLinks ? <TourShareLinksPanel slug={doc.slug} /> : null}
        </div>

        <div className="fg-author__foot">
          <button className="fg-author__btn" onClick={() => void save()} disabled={busy || !validation.ok}>
            <Save size={13} /> Save draft
          </button>
          <button
            className="fg-author__btn fg-author__btn--primary"
            onClick={() => void publish()}
            disabled={busy || draftVersion == null}
          >
            <Upload size={13} /> Publish
          </button>
          {status ? <span className="fg-author__note" style={{ flex: 1 }}>{status}</span> : null}
        </div>
      </aside>
    </>
  )
}
